import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  IoCheckmarkCircleOutline,
  IoDownloadOutline,
  IoDocumentTextOutline,
  IoSaveOutline,
  IoServerOutline,
  IoTimeOutline,
  IoWarningOutline,
  IoCopyOutline,
} from 'react-icons/io5';
import EditableField from './components/EditableField';
import ProgressStepper from './components/ProgressStepper';
import SystemDataTable from './components/SystemDataTable';
import ViewDataSetModal from './components/ViewDataSetModal';
import AuditHistoryModal from './components/AuditHistoryModal';
import {
  finalizeNarrativeReportApi,
  previewNarrativeReportApi,
  getAuditHistoryApi,
  getDataSetApi,
  getNarrativeReportApi,
  saveNarrativeReportApi,
} from './narrativeReportsApi';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { setLeavePrompt } from '../manage-users/usersSlice';
import { parseUserInfo } from '../../utilities/common';
import LoadingButton from '../../components/LoadingButton';



const NarrativeReportPage = ({ mode = 'edit' }) => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));
  const isViewMode = mode === 'view';
  const [auditVersion, setAuditVersion] = useState(() => new URLSearchParams(location.search).get('auditVersion'));
  const [report, setReport] = useState(null);
  const [activeSectionId, setActiveSectionId] = useState('');
  const [narrative, setNarrative] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(isViewMode ? 'generate' : 'template');
  const [showDataSet, setShowDataSet] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showFinalize, setShowFinalize] = useState(false);
  const [auditHistory, setAuditHistory] = useState(null);
  const [dataset, setDataset] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    getNarrativeReportApi(orgId, id, auditVersion)
      .then((data) => {
        setReport(data);
        setNarrative(data.narrative || '');
        setActiveSectionId(data.sections[0]?.id || '');
      })
      .catch(() => { })
      .finally(() => setIsLoading(false));
    return () => { dispatch(setLeavePrompt(false)); };
  }, [orgId, id, auditVersion, dispatch]);

  const activeSection = useMemo(() => (
    report?.sections.find((section) => section.id === activeSectionId)
  ), [report, activeSectionId]);

  const currentStepIndex = report?.status === 'finalized' ? 2 : step === 'generate' ? 1 : 0;

  const updateSection = useCallback((updater) => {
    setReport((current) => ({
      ...current,
      sections: current.sections.map((section) => (
        section.id === activeSectionId ? updater(section) : section
      )),
    }));
  }, [activeSectionId]);

  const handleFieldChange = (fieldId, value) => {
    updateSection((section) => ({
      ...section,
      editableFields: section.editableFields.map((field) => (
        field.id === fieldId ? { ...field, value } : field
      )),
    }));
    setErrors((current) => ({ ...current, [fieldId]: null }));
    dispatch(setLeavePrompt(true));
  };

  const validate = () => {
    const nextErrors = {};
    report.sections.forEach((section) => {
      section.editableFields.forEach((field) => {
        if (field.required && !field.value) {
          nextErrors[field.id] = t('This field is required');
        }
      });
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const countWarnings = () => report.sections.reduce((total, section) => (
    total + section.systemFields.filter((field) => (
      field.quality.status === 'Estimated' || field.quality.status === 'Missing'
    )).length
  ), 0);

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const saved = await saveNarrativeReportApi(orgId, report.id, report.sections);
      setReport(saved);
      await dispatch(setLeavePrompt(false));
      if (auditVersion) {
        setAuditVersion(null);
        history.replace(location.pathname);
      }
      getAuditHistoryApi(orgId, report.id).then((res) => setAuditHistory(res.data ?? []));
      toast.success(t('Progress saved successfully'));
    } catch (e) {
      toast.error(t('Failed to save narrative report'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!validate()) {
      toast.error(t('Please complete all required fields before continuing'));
      return;
    }

    try {
      setIsLoading(true);
      // Preview sends current UI state to backend — no DB writes, status unchanged
      const result = await previewNarrativeReportApi(orgId, report.id, report.sections);
      setNarrative(result.narrative);
      setStep('generate');
      dispatch(setLeavePrompt(false));
      toast.success(t('Preview ready'));
    } catch (e) {
      toast.error(t('Failed to generate preview'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalize = async () => {
    try {
      setIsLoading(true);
      // Finalize sends current UI sections — backend saves, validates, generates + locks
      await finalizeNarrativeReportApi(orgId, report.id, report.sections);
      setShowFinalize(false);
      await dispatch(setLeavePrompt(false));
      toast.success(t('Report finalized successfully'));
      history.push('/narrative-reports');
    } catch (e) {
      toast.error(e.message || t('Failed to finalize report'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyClipboard = async () => {
    const targetElement = document.querySelector('.narrative-document-card .narrative-preview');
    function getHtmlWithDeepStyles(element) {
      const clone = element.cloneNode(true);
      const originals = element.querySelectorAll('*');
      const clones = clone.querySelectorAll('*');
      const criticalStyles = [
        'color', 'background-color', 'background-image', 'font-family', 'font-size',
        'font-weight', 'line-height', 'text-align', 'text-decoration', 'padding',
        'margin', 'border', 'border-radius', 'display', 'flex-direction',
        'justify-content', 'align-items', 'gap', 'width', 'height', 'opacity', 'box-shadow'
      ];

      const applyStyles = (orig, cln) => {
        const computed = window.getComputedStyle(orig);
        let inlineStyle = "";
        criticalStyles.forEach(prop => {
          const value = computed.getPropertyValue(prop);
          if (value) {
            inlineStyle += `${prop}: ${value}; `;
          }
        });
        if (orig.tagName === 'TABLE') {
          inlineStyle += `border-collapse: collapse !important; border: 1px solid #dfe4ea !important; `;
        }

        if (orig.tagName === 'TH' || orig.tagName === 'TD') {
          inlineStyle += `border: 1px solid #dfe4ea !important; `;
        }

        cln.style.cssText = inlineStyle;
      };
      applyStyles(targetElement, clone);
      originals.forEach((origEl, index) => {
        applyStyles(origEl, clones[index]);
      });

      return clone.outerHTML;
    }
    const htmlContent = getHtmlWithDeepStyles(targetElement);
    const textContent = targetElement.innerText;

    try {
      const blobHtml = new Blob([htmlContent], { type: 'text/html' });
      const blobText = new Blob([textContent], { type: 'text/plain' });

      const data = [new ClipboardItem({
        'text/html': blobHtml,
        'text/plain': blobText
      })];

      await navigator.clipboard.write(data);
      toast.success(t('Report content copied to clipboard'));
    } catch (err) {
      toast.error(t('Failed to copy report content to clipboard'));
    }
  };

  const handleExport = async () => {
    const fileName = report.name || 'narrative-report';
    try {
      setIsLoading(true);
      const element = document.querySelector('.narrative-preview');
      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: 920,
        onclone: (clonedDoc) => {
          const el = clonedDoc.querySelector('.narrative-preview');
          if (el) {
            el.style.width = '920px';
            el.style.maxWidth = '920px';
            el.style.minWidth = 'unset';
            el.style.boxSizing = 'border-box';
          }
        },
      });

      const doc = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 32;
      const contentWidth = pageWidth - margin * 2;
      const pageContentHeightPts = pageHeight - margin * 2;
      const pxPerPt = canvas.width / contentWidth;
      const pageContentHeightPx = Math.floor(pageContentHeightPts * pxPerPt);
      const ctx = canvas.getContext('2d');

      const findSafeBreak = (idealY) => {
        const searchRange = Math.min(80, Math.floor(pageContentHeightPx * 0.05));
        const startY = Math.max(0, idealY - searchRange);
        const chunkH = idealY - startY + 1;
        const { data } = ctx.getImageData(0, startY, canvas.width, chunkH);
        for (let row = chunkH - 1; row >= 0; row--) {
          let isBlank = true;
          const rowStart = row * canvas.width * 4;
          for (let x = 0; x < canvas.width; x += 2) {
            const i = rowStart + x * 4;
            if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) {
              isBlank = false;
              break;
            }
          }
          if (isBlank) return startY + row;
        }
        return idealY;
      };

      let currentY = 0;
      let pageIdx = 0;
      while (currentY < canvas.height) {
        if (pageIdx > 0) doc.addPage();
        const idealEnd = currentY + pageContentHeightPx;
        const actualEnd = idealEnd >= canvas.height ? canvas.height : findSafeBreak(idealEnd);
        const srcH = actualEnd - currentY;
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        const sliceCtx = sliceCanvas.getContext('2d');
        sliceCtx.drawImage(canvas, 0, currentY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        const sliceImgData = sliceCanvas.toDataURL('image/jpeg', 0.85);
        doc.addImage(sliceImgData, 'JPEG', margin, margin, contentWidth, srcH / pxPerPt);
        currentY = actualEnd;
        pageIdx++;
      }

      doc.save(`${fileName}.pdf`);
    } catch (e) {
      toast.error(t('Failed to export report'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && !report) {
    return (
      <div className="p-4">
        <Spinner animation="grow" />
      </div>
    );
  }

  if (!report) return null;

  const renderHeader = (subtitle, actions) => (
    <>
      <div className="narrative-editor-header">
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            <Button variant="link" className="narrative-back-link mr-3 p-0" onClick={() => history.push('/narrative-reports')}>
              {t('← Back')}
            </Button>
            <div className="narrative-header-icon mr-3">
              <IoDocumentTextOutline />
            </div>
            <div className="narrative-header-title">
              <div className="font-weight-bold">
                {auditVersion && <span className="text-muted mr-1">[{auditVersion}]</span>}
                {report.name || t('Narrative Disclosure Generator')}
              </div>
              <div className="text-muted small">{subtitle || t('{{framework}} disclosure report', { framework: report.framework })}</div>
            </div>
          </div>
          <div className="narrative-header-actions">
            <LoadingButton isLoading={isLoading} />
            {actions}
          </div>
        </div>
      </div>
      <div className="narrative-stepper-wrap">
        <ProgressStepper currentStep={currentStepIndex} />
      </div>
    </>
  );

  if (step === 'generate') {
    return (
      <div className="narrative-prototype-shell">
        {renderHeader(null, (
          <div className="d-flex align-items-center">
            {!isViewMode && (
              <Button variant="outline-secondary" className="mr-2" onClick={() => setStep('template')}>
                {t('Back to Editor')}
              </Button>
            )}
            <Button variant="outline-secondary" className="mr-2" onClick={() => {
              setShowDataSet(true);
              if (!dataset) {
                getDataSetApi(orgId, report.id).then((res) => setDataset(res.data ?? []));
              }
            }}>
              <IoServerOutline className="mr-1" />
              {t('Data Set')}
            </Button>
            <Button variant="outline-secondary" className="mr-2" onClick={() => {
              setShowAudit(true);
              if (!auditHistory) {
                getAuditHistoryApi(orgId, report.id).then((res) => setAuditHistory(res.data ?? []));
              }
            }}>
              <IoTimeOutline className="mr-1" />
              {t('Audit History')}
            </Button>
            {!isViewMode && (
              <Button variant="outline-secondary" className="mr-2" onClick={() => setShowFinalize(true)}>
                <IoCheckmarkCircleOutline className="mr-1" />
                {t('Finalize')}
              </Button>
            )}
            <Button disabled={isLoading} variant="outline-primary" className="mr-2" onClick={() => handleCopyClipboard()}>
              <IoCopyOutline className="mr-1" />
              {t('Copy')}
            </Button>
            <Button disabled={isLoading} variant="primary" className="mr-2" onClick={() => handleExport()}>
              <IoDownloadOutline className="mr-1" />
              {t('Export PDF')}
            </Button>
          </div>
        ))}

        <div className="narrative-editor-main">
          <Card className="narrative-document-card">
            <Card.Body>
              {isViewMode && (
                <Alert variant="info">
                  <IoWarningOutline className="mr-1" />
                  {t('You are viewing this report in read-only mode.')}
                </Alert>
              )}
              <div className="narrative-preview" dangerouslySetInnerHTML={{ __html: narrative }} />
            </Card.Body>
          </Card>
        </div>

        <ViewDataSetModal show={showDataSet} onClose={() => setShowDataSet(false)} dataset={dataset} />
        <AuditHistoryModal
          show={showAudit}
          onClose={() => setShowAudit(false)}
          onRestoreVersion={(version) => {
            setShowAudit(false);
            setAuditHistory(null);
            setAuditVersion(version);
            setStep('template');
            history.push(`${location.pathname}?auditVersion=${encodeURIComponent(version)}`);
          }}
          reportName={report.name}
          reportStatus={report.status}
          auditHistory={auditHistory}
        />
        <Modal show={showFinalize} onHide={() => setShowFinalize(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('Finalize Report')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('Are you sure you want to mark this report as completed? Once finalized, the report status will be updated and this action will be recorded in the audit trail.')}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowFinalize(false)}>{t('Cancel')}</Button>
            <Button variant="primary" onClick={handleFinalize} disabled={isLoading}>{t('Finalize Report')}</Button>
          </Modal.Footer>
        </Modal>
      </div>
    );
  }

  return (
    <div className="narrative-prototype-shell">
      {renderHeader(null, (
        <Button variant="outline-secondary" onClick={handleSave} disabled={isLoading}>
          <IoSaveOutline className="mr-1" />
          {t('Save Progress')}
        </Button>
      ))}

      <div className="narrative-template-workspace">
        <aside className="narrative-section-sidebar">
          <h5 className="mb-3">{t('Sections')}</h5>
          {report.sections.map((section) => (
            <button
              key={section.id}
              type="button"
              onClick={() => setActiveSectionId(section.id)}
              className={`narrative-section-button ${section.id === activeSectionId ? 'active' : ''}`}
            >
              <span>{section.name}</span>
              {section.completed ? (
                <IoCheckmarkCircleOutline className="text-success" />
              ) : (
                <IoWarningOutline className="text-muted" />
              )}
            </button>
          ))}
        </aside>

        <main className="narrative-template-main">
          {activeSection && (
            <div className="narrative-template-content">
              <div className="mb-4">
                <h4>{activeSection.name}</h4>
                <p className="text-muted mb-0">{t('Complete all fields and review system-populated data below')}</p>
              </div>

              {activeSection.systemFields.length > 0 && (
                <section className="mb-4">
                  <h5 className="mb-3">{t('System Data')}</h5>
                  <Card>
                    <SystemDataTable fields={activeSection.systemFields} />
                  </Card>
                  {activeSection.systemFields.some((f) => ['water-trend', 'waste-trend'].includes(f.id)) && (
                    <Alert variant="info" className="mt-3 mb-0">
                      {t('Note: These fields require you to set up EPS with the activity types Water (with units of m3) and Waste (with units of tonnes).')}
                    </Alert>
                  )}
                </section>
              )}

              {activeSection.editableFields.length > 0 && (
                <section className="mb-4">
                  {activeSection.editableFields.map((field) => (
                    <EditableField
                      key={field.id}
                      field={field}
                      value={field.value}
                      error={errors[field.id]}
                      onChange={(value) => handleFieldChange(field.id, value)}
                    />
                  ))}
                </section>
              )}

              <div className="narrative-complete-box">
                <Form.Check
                  type="checkbox"
                  id={`complete-${activeSection.id}`}
                  checked={!!activeSection.completed}
                  label={t('Mark as Completed')}
                  onChange={(event) => {
                    updateSection((section) => ({ ...section, completed: event.target.checked }));
                    dispatch(setLeavePrompt(true));
                  }}
                />
              </div>
            </div>
          )}

          <div className="narrative-bottom-bar">
            {countWarnings() > 0 && (
              <Badge variant="warning" className="mr-auto">
                {countWarnings()} data warning{countWarnings() !== 1 ? 's' : ''}
              </Badge>
            )}
            <Button variant="primary" size="lg" onClick={handleGenerate} disabled={isLoading}>
              {t('Preview Report')}
            </Button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default NarrativeReportPage;
